// Simple toast service for DaisyUI notifications
import { getCurrentInstance } from 'vue';

// Get the current Vue app instance for accessing global properties
const getAppInstance = () => {
  const instance = getCurrentInstance();
  return instance?.appContext.app.config.globalProperties || instance?.proxy || null;
};

// Create a simple toast notification using DaisyUI classes
const createToast = (message, type = 'info', duration = 3000) => {
  // Fallback to console if DOM is not available
  if (typeof document === 'undefined') {
    console.log(`[${type.toUpperCase()}] ${message}`);
    return;
  }

  // Create toast container if it doesn't exist
  let toastContainer = document.getElementById('toast-container');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'toast-container';
    toastContainer.className = 'toast toast-top toast-end';
    toastContainer.style.position = 'fixed';
    toastContainer.style.top = '1rem';
    toastContainer.style.right = '1rem';
    toastContainer.style.zIndex = '2147483647'; // Maximum z-index value
    toastContainer.style.pointerEvents = 'none';
    document.body.appendChild(toastContainer);
  }

  // Create the alert element
  const alert = document.createElement('div');
  const alertClass = {
    success: 'alert-success',
    error: 'alert-error',
    warning: 'alert-warning',
    info: 'alert-info',
  }[type] || 'alert-info';

  alert.className = `alert ${alertClass} shadow-lg mb-2`;
  alert.style.pointerEvents = 'auto';
  alert.style.transform = 'translateX(100%)';
  alert.style.opacity = '0';
  alert.style.transition = 'all 0.3s ease-out';
  alert.style.backgroundColor = type === 'success' ? '#10b981' : 
                                type === 'error' ? '#ef4444' : 
                                type === 'warning' ? '#f59e0b' : '#3b82f6';
  alert.style.color = '#ffffff';
  alert.style.border = 'none';
  alert.style.minWidth = '300px';
  alert.style.maxWidth = '500px';
  alert.style.position = 'relative';
  alert.style.zIndex = '2147483647';
  alert.innerHTML = `
    <div>
      <span>${message}</span>
    </div>
  `;

  // Add to container
  toastContainer.appendChild(alert);

  // Animate in
  setTimeout(() => {
    alert.style.transform = 'translateX(0)';
    alert.style.opacity = '1';
  }, 10);

  // Auto remove after duration
  setTimeout(() => {
    if (alert.parentNode) {
      alert.style.opacity = '0';
      alert.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (alert.parentNode) {
          alert.parentNode.removeChild(alert);
        }
      }, 300);
    }
  }, duration);
};

// Export toast functions
export const toast = {
  success: (message, duration) => createToast(message, 'success', duration),
  error: (message, duration) => createToast(message, 'error', duration),
  warning: (message, duration) => createToast(message, 'warning', duration),
  info: (message, duration) => createToast(message, 'info', duration),
};

// Install function for Vue 3
export const installToast = (app) => {
  app.config.globalProperties.$toast = toast;
  app.provide('toast', toast);
};

// Enhanced error handling service
export const showErrorToast = (error) => {
  const appInstance = getAppInstance();
  
  if (!checkIfAxiosError(error)) {
    toast.error(error);
    return;
  }

  const axiosErrorData = error.response.data;

  if (checkForErrorArray(axiosErrorData)) {
    toast.error(axiosErrorData[0]);
    return;
  }

  if (checkForServiceObject(axiosErrorData)) {
    toast.error(axiosErrorData.errors[0]);
    return;
  }

  toast.error(axiosErrorData);
};

// Helper functions
const checkIfAxiosError = (error) => !!error.response;
const checkForErrorArray = (axiosErrorData) => Array.isArray(axiosErrorData);
const checkForServiceObject = (axiosErrorData) => typeof axiosErrorData === 'object' && 'success' in axiosErrorData;
